import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { BRAND, useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { sendChunks, startUpload } from "@/lib/chunkedUpload";
import { PageHero } from "@/components/layout/PageHero";

const SHARE_CACHE = "fmo-share";

async function sharedFiles() {
  const cache = await caches.open(SHARE_CACHE);
  const keys = await cache.keys();
  return Promise.all(keys.map(async (req) => {
    const res = await cache.match(req);
    const name = decodeURIComponent(res.headers.get("X-File-Name") || "app.apk");
    return { req, file: new File([await res.blob()], name, { type: "application/vnd.android.package-archive" }) };
  }));
}

async function whoAmI() {
  try {
    await api.get("/auth/me");
    return "/admin";
  } catch {
    try {
      await api.get("/dev/auth/me");
      return "/dev";
    } catch {
      return null;
    }
  }
}

// Web Share Target landing: APKs shared from the phone are uploaded for the logged-in admin/developer.
export default function SharePage() {
  const { t } = useI18n();
  const [state, setState] = useState({ phase: "loading", rows: [] });

  useEffect(() => {
    (async () => {
      const files = "caches" in window ? await sharedFiles() : [];
      if (!files.length) return setState({ phase: "empty", rows: [] });
      const base = await whoAmI();
      if (!base) {
        sessionStorage.setItem("fmo_after_login", "/partage");
        return setState({ phase: "login", rows: files.map((f) => ({ name: f.file.name })) });
      }
      const rows = files.map((f) => ({ name: f.file.name, status: "queued" }));
      setState({ phase: "upload", rows: [...rows] });
      for (const [i, f] of files.entries()) {
        try {
          const init = await startUpload({ base, appId: null, file: f.file, version: "" });
          const res = await sendChunks({ base, uploadId: init.upload_id, file: f.file, chunkSize: init.chunk_size, totalChunks: init.total_chunks, onProgress: () => {} });
          rows[i] = { name: f.file.name, status: "done", msg: (res.result === "new_app" ? t("bulk.newApp", { name: res.app.name }) : t("bulk.newVersion", { name: res.app.name, v: res.version.label })) + (res.dedup ? ` · ${t("upload.dedup")}` : "") };
        } catch (err) {
          rows[i] = { name: f.file.name, status: "error", msg: translateError(err, t) };
        }
        await (await caches.open(SHARE_CACHE)).delete(f.req);
        setState({ phase: "upload", rows: [...rows], base });
      }
      setState({ phase: "done", rows: [...rows], base });
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="share-page">
      <PageHero eyebrow={BRAND} title={t("share.title")} />
      <div className="mx-auto max-w-3xl space-y-4 px-4 sm:px-8">
        {state.phase === "loading" && <Loader2 className="h-6 w-6 animate-spin text-[var(--gold)]" />}
        {state.phase === "empty" && <p className="text-sm text-neutral-400" data-testid="share-empty">{t("share.empty")}</p>}
        {state.phase === "login" && (
          <div className="card-carbon space-y-3 rounded-2xl p-5" data-testid="share-login">
            <p className="text-sm text-neutral-200">{t("share.login")}</p>
            <div className="flex flex-wrap gap-2">
              <Link to="/espace-developpeur/connexion" className="btn-gold btn-sm" data-testid="share-dev-login">{t("nav.devSpace")}</Link>
              <Link to="/admin/login" className="btn-red btn-sm" data-testid="share-admin-login">{t("nav.admin")}</Link>
            </div>
          </div>
        )}
        <ul className="space-y-2" data-testid="share-list">
          {state.rows.map((r, i) => (
            <li key={i} className="card-carbon flex min-w-0 items-center gap-2 rounded-xl p-3 text-sm">
              {r.status === "queued" ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[var(--gold)]" /> : <Share2 className="h-4 w-4 shrink-0 text-[var(--gold)]" />}
              <span dir="ltr" className="min-w-0 truncate text-neutral-300">{r.name}</span>
              {r.msg && <span className={r.status === "error" ? "text-red-300" : "text-emerald-300"} data-testid="share-result">{r.msg}</span>}
            </li>
          ))}
        </ul>
        {state.phase === "done" && <Link to={state.base === "/admin" ? "/admin" : "/espace-developpeur"} className="btn-gold btn-sm" data-testid="share-back">{t("share.back")}</Link>}
      </div>
    </div>
  );
}
